import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './styles/tokens.css'
import './styles/base.css'
import './styles/app.css'
import { AuthProvider } from './lib/auth'
import { StoreProvider } from './lib/store'
import { parseThemeParam } from './lib/router'
import { bootTheme } from './lib/theme'
import App from './App'
import { RegionRoot } from './lib/currentRegion'
import { Boundary } from './components/Boundary'
import { toast } from './ui/Toast'
import { isTypingTarget } from './lib/typing'
import { isBusy, onControllerChange } from './lib/swUpdate'

/** Would a reload right now throw something away? See `lib/swUpdate.ts`. */
function busyNow(): boolean {
  return isBusy({
    typing: isTypingTarget(document.activeElement),
    dialogOpen: document.querySelector('[role="dialog"]') != null,
    unsentText: Array.from(document.querySelectorAll('textarea')).some(t => t.value.trim() !== ''),
  })
}

// Before the first render, not in an effect: an effect runs after the browser
// has already painted, so a dark-mode phone would flash a paper-white screen
// on every cold start. `?theme=` in the URL wins for this load only and is not
// stored — that is the audit harness's dark render.
bootTheme(parseThemeParam(window.location.href))

// The store wraps the auth provider rather than sitting inside it: the cache
// is not the session's, it outlives every sign-in, and a screen's query has to
// be able to retry a token that was rejected for being a second too new.
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <StoreProvider>
      <AuthProvider>
        {/* The last resort. Each screen and sheet has its own boundary (see
            `Boundary.tsx`); this one is for the shell itself, where Retry
            would only throw again, so it offers a reload. */}
        <Boundary where="root" reload>
          {/* C2.2: publishes the current region and keys the app on it, so a
              region switch remounts the tree under the new region's zone and
              feed (see `currentRegion.ts`). Inside the providers: it reads the
              session, the profile and the stays. */}
          <RegionRoot>
            <App />
          </RegionRoot>
        </Boundary>
      </AuthProvider>
    </StoreProvider>
  </StrictMode>
)

// Registered only in production: a service worker in dev serves stale bundles
// and wastes an afternoon.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  // Reload once the replacement worker takes over, so a deploy reaches an
  // installed phone without anyone clearing anything. Registering and
  // walking away is what left a home-screen install running code from a
  // previous deploy with no way to notice.
  //
  // Only a page that was already controlled has old code to replace. `sw.js`
  // claims clients on activate, so the first install fires `controllerchange`
  // too, with the controller already set — the old `!controller` guard never
  // tripped and every first visit reloaded, mid-sign-in included (UI-audit
  // a4-08). So `controlled` is read here, before anything registers, and
  // the listener goes on before `register` so no change can slip past it.
  // And not while someone is typing or has a sheet up: then the toast offers
  // the reload, and the next foreground tries again. See `lib/swUpdate.ts`.
  let controlled = !!navigator.serviceWorker.controller
  let reloading = false
  let pending = false
  const reload = () => { reloading = true; pending = false; window.location.reload() }
  const decide = (wasControlled: boolean) => onControllerChange({
    wasControlled, hasController: !!navigator.serviceWorker.controller, reloading, busy: busyNow(),
  })
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const wasControlled = controlled
    controlled = !!navigator.serviceWorker.controller
    const act = decide(wasControlled)
    if (act === 'reload') reload()
    else if (act === 'defer') {
      pending = true
      toast({ text: 'Update ready', action: { label: 'Reload', onClick: reload }, ms: 60_000 })
    }
  })

  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js')

      // An installed app can stay open for days. Check on every foreground so
      // it does not need a cold start to notice a deploy — and take a reload
      // that was put off for a field or a sheet, if nothing is open now.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return
        if (pending && decide(true) === 'reload') { reload(); return }
        void reg.update()
      })
    } catch {
      // A failed registration must never stop the app loading.
    }
  })
}
